import { API_CONFIG, API_ENDPOINTS, PLATFORM_ENDPOINTS, getHeaders, getAuthToken, OAUTH_CONFIG } from '../../config/config';
import { ErrorResponse } from '../../types';

// Re-export config values used by other modules
export { API_CONFIG, API_ENDPOINTS, PLATFORM_ENDPOINTS, getHeaders, getAuthToken, OAUTH_CONFIG };
export type { ErrorResponse };

/** Error subclass for HTTP 404 responses, used to distinguish "not found" from other failures. */
export class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotFoundError';
  }
}

// Custom event name dispatched when the backend returns 401 (token expired).
// useAppAuth listens for this to trigger federated logout.
export const AUTH_TOKEN_EXPIRED_EVENT = 'auth:token-expired';

let _logoutDispatched = false;

/**
 * When a completed HTTP response is 401, notify the auth layer once (same as {@link authFetch}).
 * Use for transports that bypass fetch, e.g. XMLHttpRequest multipart uploads.
 */
export function notifyAuthResponseStatus(status: number): void {
  if (status === 401 && !_logoutDispatched) {
    _logoutDispatched = true;
    window.dispatchEvent(new CustomEvent(AUTH_TOKEN_EXPIRED_EVENT));
  }
}

/**
 * Thin wrapper around fetch that auto-detects HTTP 401 from the backend and
 * fires a custom DOM event so the auth layer can trigger logout. A module-level
 * flag prevents duplicate dispatches when several requests fail concurrently.
 */
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  notifyAuthResponseStatus(response.status);
  return response;
}

/** Reset the dispatch guard (called from useAppAuth after handling the event). */
export function resetTokenExpiredFlag(): void {
  _logoutDispatched = false;
}

/**
 * Get headers for API calls.
 * The backend derives org from path or site_id and validates access.
 */
export function getHeadersWithOrg(): HeadersInit {
  return getHeaders();
}

/**
 * Get headers for multipart uploads (no Content-Type so browser sets boundary).
 * Includes auth token. Org is derived from path/site_id on the backend.
 */
export function getMultipartHeaders(_orgId: string): HeadersInit {
  const token = getAuthToken();
  return {
    'x-ims-clientid': OAUTH_CONFIG.CLIENT_ID,
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

/**
 * Generic API call helper that handles the common try/catch/response.ok/json pattern.
 * Use this for simple request -> JSON response functions. Skip for streaming,
 * special error handling, or functions that inspect response headers.
 */
export async function apiCall<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await authFetch(url, init);
  if (!response.ok) {
    const errorData: ErrorResponse = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    // FastAPI 422 returns `detail` as an array of validation-error objects, not a string.
    // Only use `detail` when it is a string; fall through to `error` or the status code otherwise.
    const detail = typeof errorData.detail === 'string' ? errorData.detail : null;
    throw new Error(detail || errorData.error || `Request failed: ${response.status}`);
  }
  // Some endpoints (e.g. DELETE) return 204 No Content with an empty body.
  // Calling response.json() on an empty body throws "Unexpected end of JSON input".
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
}
